import React, { useState } from 'react';

import { Input } from 'semantic-ui-react';

import _debounce from 'lodash/debounce';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';

import {
  CreateThemeTemplateDropdown,
  ThemeTemplatesTable,
  ThemeTemplateModal,
  ThemeTemplateTypeDropdown,
} from 'components/modules/theme-templates/components';
import { useThemeTemplates } from 'components/modules/theme-templates/hooks';
import { DismissibleMessage, ErrorBoundary } from 'components/modules/utils/components';
import { DEFAULT_ERROR_MESSAGE } from 'libs/errors/constants';

import { getThemeTemplateModalMode } from './ThemeTemplatesTab.utils';

import './ThemeTemplatesTab.scss';

const propTypes = {};

const defaultProps = {};

export default function ThemeTemplatesTab() {
  const [templateToView, setTemplateToView] = useState(null);
  const [templateToApply, setTemplateToApply] = useState(null);
  const [templateToEdit, setTemplateToEdit] = useState(null);
  const [websiteTemplateToCreate, setWebsiteTemplateToCreate] = useState(null);
  const [createBlankTemplateModalOpen, setCreateBlankTemplateModalOpen] = useState(false);

  const {
    error,
    loading,
    templateType,
    onSearchQueryChange,
    themeTemplates,
    createThemeTemplate,
    editThemeTemplate,
    applyThemeTemplate,
    createTemplateFromWebsite,
    onTemplateTypeChange,
  } = useThemeTemplates({ forceFetch: true });

  function onCloseTemplateModal() {
    setTemplateToView(null);
    setTemplateToApply(null);
    setTemplateToEdit(null);
    setWebsiteTemplateToCreate(null);
    setCreateBlankTemplateModalOpen(false);
  }

  function onCreateBlankTemplate() {
    setCreateBlankTemplateModalOpen(true);
  }

  function onCreateWebsiteTemplate() {
    setWebsiteTemplateToCreate(createTemplateFromWebsite());
  }

  function onViewTemplate(template) {
    setTemplateToView(template);
  }

  function onOpenApplyTemplate(template) {
    setTemplateToApply(template);
  }

  async function onApplyTemplate(template) {
    await applyThemeTemplate(template);

    onCloseTemplateModal();
  }

  function onOpenEditTemplate(template) {
    setTemplateToEdit(template);
  }

  async function onEditTemplate(template) {
    const { id } = template;

    if (_isNil(id)) {
      await createThemeTemplate(template);
    } else {
      await editThemeTemplate(template);
    }

    onCloseTemplateModal();
  }

  const isModalOpen =
    ![templateToView, templateToApply, templateToEdit, websiteTemplateToCreate].every(_isEmpty) ||
    createBlankTemplateModalOpen;
  const modalMode = getThemeTemplateModalMode({
    templateToView,
    templateToApply,
    templateToEdit,
    websiteTemplateToCreate,
    createBlankTemplateModalOpen,
  });
  const modalTemplate =
    templateToView || templateToApply || templateToEdit || websiteTemplateToCreate;

  return (
    <ErrorBoundary
      showFallback={!_isEmpty(error)}
      fallback={
        <DismissibleMessage initialVisible content={error || DEFAULT_ERROR_MESSAGE} error />
      }
    >
      <div className="theme-templates-tab">
        <div className="controls-container">
          <div>
            <Input
              fluid
              className="theme-templates-search"
              placeholder="Search theme template"
              onChange={_debounce(onSearchQueryChange, 500)}
              loading={loading}
              icon="search"
            />
          </div>
          <div>
            <ThemeTemplateTypeDropdown value={templateType} onChange={onTemplateTypeChange} />
          </div>
          <div>
            <CreateThemeTemplateDropdown
              onCreateBlankTheme={onCreateBlankTemplate}
              onCreateWebsiteTheme={onCreateWebsiteTemplate}
            />
          </div>
        </div>
        <div className="table-container">
          <ThemeTemplatesTable
            themeTemplates={themeTemplates}
            onApplyTemplate={onOpenApplyTemplate}
            onEditTemplate={onOpenEditTemplate}
            onViewTemplate={onViewTemplate}
            loadingTemplates={loading}
          />
        </div>
      </div>
      {isModalOpen && (
        <ThemeTemplateModal
          open={isModalOpen}
          mode={modalMode}
          template={modalTemplate}
          onClose={onCloseTemplateModal}
          onApplyTemplate={onApplyTemplate}
          onEditTemplate={onEditTemplate}
        />
      )}
    </ErrorBoundary>
  );
}

ThemeTemplatesTab.propTypes = propTypes;
ThemeTemplatesTab.defaultProps = defaultProps;
